import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import ResourcesModal from './ResourcesModal'
import './Tutorials.css'

interface Resource {
  id: string
  type: 'video' | 'code' | 'document' | 'link'
  title: string
  url: string
}

interface Tutorial {
  id: string
  title: string
  description: string
  category: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  duration: string
  videoUrl?: string
  codeUrl?: string
  resources?: Resource[]
  createdAt: string
}

const Tutorials = () => {
  const { user } = useAuth()
  const [tutorials, setTutorials] = useState<Tutorial[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [showSaved, setShowSaved] = useState(false)
  const [openResourcesModal, setOpenResourcesModal] = useState<string | null>(null)

  useEffect(() => {
    // Load tutorials from localStorage
    const stored = localStorage.getItem('tutorials')
    if (stored) {
      try {
        setTutorials(JSON.parse(stored))
      } catch (e) {
        console.error('Error loading tutorials:', e)
      }
    } else {
      // Demo content
      const demo: Tutorial[] = [
        {
          id: 'tut_1',
          title: 'getting started with sonic pi',
          description: 'Learn the basics of live coding with Sonic Pi. Create your first generative ambient piece and understand the fundamentals of algorithmic composition.',
          category: 'Sonic Pi',
          difficulty: 'beginner',
          duration: '45 min',
          videoUrl: '#',
          codeUrl: '#',
          createdAt: new Date().toISOString(),
        },
        {
          id: 'tut_2',
          title: 'strudel patterns and sequences',
          description: 'Explore pattern-based composition in Strudel. Master the syntax for creating complex rhythmic structures and melodic sequences.',
          category: 'Strudel',
          difficulty: 'intermediate',
          duration: '60 min',
          videoUrl: '#',
          codeUrl: '#',
          createdAt: new Date().toISOString(),
        },
        {
          id: 'tut_3',
          title: 'playtronica hardware integration',
          description: 'Connect Playtronica devices to your live coding setup. Learn MIDI mapping, sensor calibration, and creating interactive sound installations.',
          category: 'Playtronica',
          difficulty: 'intermediate',
          duration: '90 min',
          videoUrl: '#',
          codeUrl: '#',
          createdAt: new Date().toISOString(),
        },
        {
          id: 'tut_4',
          title: 'touch designer visual synthesis',
          description: 'Create generative visuals that respond to audio. Integrate Touch Designer with your live coding environment for immersive audiovisual performances.',
          category: 'Touch Designer',
          difficulty: 'advanced',
          duration: '120 min',
          videoUrl: '#',
          codeUrl: '#',
          createdAt: new Date().toISOString(),
        },
      ]
      setTutorials(demo)
      localStorage.setItem('tutorials', JSON.stringify(demo))
    }
  }, [])

  const categories = ['All', 'Sonic Pi', 'Strudel', 'Playtronica', 'Touch Designer']
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all')

  // Load saved tutorials - refresh on component update
  const [savedTutorials, setSavedTutorials] = useState<string[]>([])
  
  useEffect(() => {
    if (user) {
      try {
        const saved = JSON.parse(localStorage.getItem(`saved_tutorials_${user.id}`) || '[]')
        setSavedTutorials(saved)
      } catch (e) {
        console.error('Error loading saved tutorials:', e)
      }
    }
  }, [user, tutorials])

  const handleToggleSave = (tutorialId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!user) return

    try {
      const saved = JSON.parse(localStorage.getItem(`saved_tutorials_${user.id}`) || '[]')
      const isSaved = saved.includes(tutorialId)
      const updated = isSaved
        ? saved.filter((id: string) => id !== tutorialId)
        : [...saved, tutorialId]
      localStorage.setItem(`saved_tutorials_${user.id}`, JSON.stringify(updated))
      setSavedTutorials(updated)
    } catch (e2) {
      console.error('Error toggling save:', e2)
    }
  }

  const handleResourcesUpdate = (tutorialId: string, resources: Resource[]) => {
    const updated = tutorials.map(t =>
      t.id === tutorialId ? { ...t, resources } : t
    )
    setTutorials(updated)
    localStorage.setItem('tutorials', JSON.stringify(updated))
  }

  const filteredTutorials = tutorials.filter(tut => {
    const categoryMatch = selectedCategory === 'All' || tut.category === selectedCategory
    const difficultyMatch = selectedDifficulty === 'all' || tut.difficulty === selectedDifficulty
    const searchMatch = !searchQuery ||
      tut.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tut.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tut.category.toLowerCase().includes(searchQuery.toLowerCase())
    const savedMatch = !showSaved || (user && savedTutorials.includes(tut.id))
    return categoryMatch && difficultyMatch && searchMatch && savedMatch
  })

  return (
    <div className="tutorials-container">
      <div className="tutorials-header">
        <h2 className="tutorials-title">exclusive tutorials</h2>
        <p className="tutorials-subtitle">
          Access step-by-step guides on live coding, hardware integration, and creative technology.
        </p>
      </div>

      <div className="tutorials-search">
        <input
          type="text"
          className="tutorials-search-input"
          placeholder="search tutorials..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <i className="fas fa-search"></i>
      </div>

      <div className="tutorials-filters">
        {user && (
          <div className="tutorials-filter-group">
            <label className="tutorials-filter-label">saved</label>
            <div className="tutorials-filter-buttons">
              <button
                className={`tutorials-filter-button ${showSaved ? 'active' : ''}`}
                onClick={() => setShowSaved(!showSaved)}
              >
                <i className="fas fa-bookmark"></i>
                saved
              </button>
            </div>
          </div>
        )}
        <div className="tutorials-filter-group">
          <label className="tutorials-filter-label">category</label>
          <div className="tutorials-filter-buttons">
            {categories.map(cat => (
              <button
                key={cat}
                className={`tutorials-filter-button ${selectedCategory === cat ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="tutorials-filter-group">
          <label className="tutorials-filter-label">difficulty</label>
          <div className="tutorials-filter-buttons">
            <button
              className={`tutorials-filter-button ${selectedDifficulty === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedDifficulty('all')}
            >
              all
            </button>
            <button
              className={`tutorials-filter-button ${selectedDifficulty === 'beginner' ? 'active' : ''}`}
              onClick={() => setSelectedDifficulty('beginner')}
            >
              beginner
            </button>
            <button
              className={`tutorials-filter-button ${selectedDifficulty === 'intermediate' ? 'active' : ''}`}
              onClick={() => setSelectedDifficulty('intermediate')}
            >
              intermediate
            </button>
            <button
              className={`tutorials-filter-button ${selectedDifficulty === 'advanced' ? 'active' : ''}`}
              onClick={() => setSelectedDifficulty('advanced')}
            >
              advanced
            </button>
          </div>
        </div>
      </div>

      <div className="tutorials-grid">
        {filteredTutorials.length === 0 ? (
          <div className="tutorials-empty">
            <p>no tutorials found</p>
          </div>
        ) : (
          filteredTutorials.map(tutorial => {
            const isSaved = user && savedTutorials.includes(tutorial.id)
            const hasResources = tutorial.resources && tutorial.resources.length > 0
            return (
              <div key={tutorial.id} className="tutorial-card-wrapper">
                <Link to={`/tutorial/${tutorial.id}`} className="tutorial-card">
                  <div className="tutorial-card-header">
                    <span className="tutorial-category">{tutorial.category}</span>
                    <span className={`tutorial-difficulty ${tutorial.difficulty}`}>
                      {tutorial.difficulty}
                    </span>
                  </div>
                  <h3 className="tutorial-card-title">{tutorial.title}</h3>
                  <p className="tutorial-card-description">{tutorial.description}</p>
                  <div className="tutorial-card-footer">
                    {user && (
                      <button
                        className={`tutorial-save-button ${isSaved ? 'saved' : ''}`}
                        onClick={(e) => handleToggleSave(tutorial.id, e)}
                        disabled={!user}
                        aria-label={isSaved ? 'Unsave tutorial' : 'Save tutorial'}
                      >
                        <i className="fas fa-bookmark"></i>
                      </button>
                    )}
                    {hasResources && (
                      <button
                        className="tutorial-resources-button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setOpenResourcesModal(tutorial.id)
                        }}
                      >
                        <i className="fas fa-folder-open"></i>
                        resources
                      </button>
                    )}
                  </div>
                </Link>
                {openResourcesModal === tutorial.id && (
                  <ResourcesModal
                    tutorialId={tutorial.id}
                    isOpen={true}
                    onClose={() => setOpenResourcesModal(null)}
                    resources={tutorial.resources || []}
                    onResourcesUpdate={(resources) => handleResourcesUpdate(tutorial.id, resources)}
                  />
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default Tutorials

